import React, { useState, useEffect } from 'react';
import { Search, ArrowRight, CheckCircle, Lock, Plus, Loader } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { baseUrl } from '../config';
import SEO from '../components/SEO';
import toast from 'react-hot-toast';  // ✅ ADDED
import footerBg from '../assets/img/toolmanagerfooter.jpg';
import '../styles/projectselector.css';

// ==================== ICONS (inline SVG — no dependency/version risk) ====================
const ICON_PATHS = {
  grid: (<><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></>),
  fileText: (<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></>),
  calculator: (<><rect x="4" y="2" width="16" height="20" rx="2" /><line x1="8" y1="6" x2="16" y2="6" /><line x1="16" y1="14" x2="16" y2="18" /><path d="M16 10h.01M12 10h.01M8 10h.01M12 14h.01M8 14h.01M12 18h.01M8 18h.01" /></>),
  users: (<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>),
  mapPin: (<><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></>),
  barChart: (<><line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /><line x1="6" y1="20" x2="6" y2="16" /></>),
  userCheck: (<><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><polyline points="17 11 19 13 23 9" /></>),
  calendar: (<><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>),
  folder: (<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />),
  scissors: (<><circle cx="6" cy="6" r="3" /><circle cx="6" cy="18" r="3" /><line x1="20" y1="4" x2="8.12" y2="15.88" /><line x1="14.47" y1="14.48" x2="20" y2="20" /><line x1="8.12" y1="8.12" x2="12" y2="12" /></>),
  sparkle: (<><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" /><path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z" /></>),
  trendingUp: (<><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></>),
  trendingDown: (<><polyline points="23 18 13.5 8.5 8.5 13.5 1 6" /><polyline points="17 18 23 18 23 12" /></>),
  megaphone: (<><path d="M3 11v2a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1z" /><path d="M15.5 8.5a5 5 0 0 1 0 7" /><path d="M18.5 5.5a9 9 0 0 1 0 13" /></>),
  image: (<><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></>),
  clapper: (<><path d="M4 11v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8H4z" /><path d="M4 11l-.88-3.12a1 1 0 0 1 .7-1.23l13.5-3.8a1 1 0 0 1 1.23.7L19.4 7" /><line x1="7.5" y1="5.9" x2="9.6" y2="9.7" /><line x1="12.3" y1="4.6" x2="14.4" y2="8.3" /></>),
  globe: (<><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></>),
  clock: (<><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>),
  lightbulb: (<><path d="M9 18h6" /><path d="M10 22h4" /><path d="M12 2a7 7 0 0 0-4 12.74V17h8v-2.26A7 7 0 0 0 12 2z" /></>),
};

const ToolIcon = ({ name, size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {ICON_PATHS[name]}
  </svg>
);

// Visual identity per tool (keyed by dbToolId) — UI only
const TOOL_VISUALS = {
  pb0000000001: { icon: 'fileText', tone: 'sky' },
  pb0000000002: { icon: 'calculator', tone: 'emerald' },
  pb0000000003: { icon: 'users', tone: 'amber' },
  pb0000000004: { icon: 'mapPin', tone: 'rose' },
  pb0000000005: { icon: 'barChart', tone: 'emerald' },
  pb0000000006: { icon: 'userCheck', tone: 'sky' },
  pb0000000007: { icon: 'calendar', tone: 'amber' },
  pb0000000008: { icon: 'folder', tone: 'teal' },
  pb0000000009: { icon: 'scissors', tone: 'rose' },
  pb0000000010: { icon: 'sparkle', tone: 'teal' },
  pb0000000011: { icon: 'trendingUp', tone: 'sky' },
  pb0000000012: { icon: 'megaphone', tone: 'orange' },
  pb0000000013: { icon: 'image', tone: 'emerald' },
  pb0000000014: { icon: 'clapper', tone: 'rose' },
  pb0000000015: { icon: 'globe', tone: 'sky' },
};

const CATEGORY_COPY = {
  "Pre-Production": "Plan smarter, save time, and build a stronger foundation for your story.",
  "Production": "Keep your shoot on schedule, on budget, and in sync.",
  "Post-Production": "Polish, promote, and put your film in front of the right audience.",
};

const IMPACT_POINTS = [
  { icon: 'clock', tone: 'teal', title: 'Reduce Production Time', text: 'Our AI tools automate repetitive tasks and streamline workflows, reducing production time by up to 40%.' },
  { icon: 'trendingDown', tone: 'amber', title: 'Lower Costs', text: 'Smart resource allocation and predictive budgeting help productions stay on budget and avoid costly overruns.' },
  { icon: 'lightbulb', tone: 'sky', title: 'Enhance Creativity', text: 'Let AI handle the logistics so your team can focus on what matters most - creating amazing content.' },
];

const ProductionToolsDashboard = ({ projectId }) => {
  const [selectedCategory, setSelectedCategory] = useState("Pre-Production");
  const [activatedToolIds, setActivatedToolIds] = useState([]);
  const [subscribedToolIds, setSubscribedToolIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activatingTool, setActivatingTool] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const toolsData = {
    "Pre-Production": [
      {
        name: "Script Analysis",
        dbToolId: "pb0000000001",
        description: "Get AI insights on your script's pacing, character development, and marketability",
        icon: "📄",
        activeLink: "/input"
      },
      {
        name: "Budgeting",
        dbToolId: "pb0000000002",
        description: "Intelligent budget forecasting based on similar successful productions",
        icon: "💰",
        activeLink: "/budget"
      },
      {
        name: "Casting Assistant",
        dbToolId: "pb0000000003",
        description: "Find the perfect talent for your roles with AI matching",
        icon: "👥",
        activeLink: "/cast"
      },
      {
        name: "Location Scouting",
        dbToolId: "pb0000000004",
        description: "Discover and manage shooting locations with advanced filtering",
        icon: "🏙️",
        activeLink: "/location"
      }
    ],
    "Production": [
      {
        name: "Progress Management",
        dbToolId: "pb0000000005",
        description: "Track your production milestones with real-time updates and forecasting",
        icon: "📊",
        activeLink: "/tracker"
      },
      {
        name: "Cast Management",
        dbToolId: "pb0000000006",
        description: "Streamline talent scheduling, communication, and documentation",
        icon: "🎭",
        activeLink: "/cast-management"
      },
      {
        name: "Scheduling Assistant",
        dbToolId: "pb0000000007",
        description: "Optimize your shooting schedule with AI to minimize costs and conflicts",
        icon: "🗓️",
        activeLink: "/schedule"
      },
      {
        name: "Documentation",
        dbToolId: "pb0000000008",
        description: "Centralized digital storage for all production documents with smart search",
        icon: "📚",
        activeLink: "/documents"
      }
    ],
    "Post-Production": [
      {
        name: "Editing",
        dbToolId: "pb0000000009",
        description: "AI-assisted editing suggestions to enhance your storytelling",
        icon: "✂️",
        activeLink: "/editing"
      },
      {
        name: "Visual Effects",
        dbToolId: "pb0000000010",
        description: "Streamlined VFX workflow management and resource allocation",
        icon: "🎬",
        activeLink: "/vfx"
      },
      {
        name: "Marketing Analyst",
        dbToolId: "pb0000000011",
        description: "Data-driven audience targeting and campaign optimization",
        icon: "📈",
        activeLink: "/post-analysis"
      },
      {
        name: "Advertisements",
        dbToolId: "pb0000000012",
        description: "Create compelling ads tailored to your target demographics",
        icon: "📣",
        activeLink: "/advertisement-promotions"
      },
      {
        name: "Posters",
        dbToolId: "pb0000000013",
        description: "Generate professional poster designs with AI assistance",
        icon: "🖼️",
        activeLink: "/postergenerationpage"
      },
      {
        name: "Trailers",
        dbToolId: "pb0000000014",
        description: "AI tools to help create engaging trailers that convert to viewers",
        icon: "🎥",
        activeLink: "/trailer"
      },
      {
        name: "Distribution",
        dbToolId: "pb0000000015",
        description: "Optimize your distribution strategy across platforms and regions",
        icon: "🌐",
        activeLink: "/distributions"
      }
    ]
  };

  async function getActivatedTools() {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(`${baseUrl}/showTools`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'project_id': String(projectId)
        }
      });

      if (!response.ok) {
        if (response.status === 401) navigate("/login");
        return;
      }

      const toolData = await response.json();
      setSubscribedToolIds(toolData.map(tool => tool.tool_id || tool.id));

    } catch (error) {
    }
  }

  async function getSubscribedTools() {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(`${baseUrl}/getAvailableTools`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'project_id': String(projectId)
        }
      });

      if (!response.ok) {
        if (response.status === 401) navigate("/login");
        return;
      }

      const toolData = await response.json();
      setActivatedToolIds(toolData.map(tool => tool.tool_id || tool.id));

    } catch (error) {
    }
  }

  // ✅ UPDATED WITH TOAST NOTIFICATIONS
  async function verifySubscriptionBeforeLaunch(tool, projectId) {
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${baseUrl}/verifyToolAccess`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ tool_id: tool.dbToolId, project_id: projectId })
      });

      if (!response.ok) {
        // ✅ AUTO RESYNC on 403
        toast.loading('Syncing your tools...', { id: 'resync' });
        await fetch(`${baseUrl}/resyncTools`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ project_id: projectId })
        });
        toast.dismiss('resync');

        // Retry verify after resync
        const retry = await fetch(`${baseUrl}/verifyToolAccess`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ tool_id: tool.dbToolId, project_id: projectId })
        });

        if (!retry.ok) {
          toast.error('Access denied. Please contact support.', { icon: '🔒', duration: 5000 });
          return false;
        }
        return true;
      }
      return true;
    } catch (error) {
      toast.error('Unable to verify access. Please try again.', { icon: '❌', duration: 5000 });
      return false;
    }
  }

  // ✅ UPDATED WITH TOAST NOTIFICATIONS
  async function activateToolForProject(tool) {
    setActivatingTool(tool.name);

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(`${baseUrl}/activateTool`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          project_id: projectId,
          tool_id: tool.dbToolId
        })
      });

      if (!response.ok) {
        const error = await response.json();
        // ✅ TOAST INSTEAD OF ALERT
        toast.error(error.detail || 'Failed to activate tool', {
          icon: '❌',
          duration: 5000,
        });
        return;
      }

      await getActivatedTools();

      // ✅ SUCCESS TOAST
      toast.success(
        <div>
          <p className="font-semibold">{tool.name} activated!</p>
          <p className="text-sm opacity-90">You can now use it for this project</p>
        </div>,
        {
          icon: '🎉',
          duration: 4000,
        }
      );

    } catch (error) {
      toast.error(`Failed to activate ${tool.name}. Please try again.`, {
        icon: '❌',
        duration: 5000,
      });
    } finally {
      setActivatingTool(null);
    }
  }

  useEffect(() => {
    if (projectId) {
      setIsLoading(true);
      Promise.all([
        getActivatedTools(),
        getSubscribedTools()
      ]).finally(() => {
        setIsLoading(false);
      });
    }
  }, [projectId]);

  const isToolActivated = (tool) => activatedToolIds.includes(tool.dbToolId);
  const isToolSubscribed = (tool) => subscribedToolIds.includes(tool.dbToolId);

  const filteredTools = toolsData[selectedCategory].filter(tool =>
    tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tool.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Same ordering as before: activated -> ready (subscribed) -> locked
  const orderedTools = [
    ...filteredTools.filter(tool => isToolActivated(tool)),
    ...filteredTools.filter(tool => !isToolActivated(tool) && isToolSubscribed(tool)),
    ...filteredTools.filter(tool => !isToolActivated(tool) && !isToolSubscribed(tool)),
  ];

  const renderToolCard = (tool) => {
    const activated = isToolActivated(tool);
    const subscribed = isToolSubscribed(tool);
    const isActivating = activatingTool === tool.name;
    const visual = TOOL_VISUALS[tool.dbToolId] || { icon: 'grid', tone: 'teal' };
    const state = activated ? 'active' : subscribed ? 'ready' : 'locked';

    return (
      <div key={tool.name} className={`ps-card ps-card--${state}`}>
        <div className="ps-card-top">
          <span className={`ps-tile ps-tone-${visual.tone}`}>
            <ToolIcon name={visual.icon} size={26} />
          </span>
          <div className="ps-card-text">
            <h3 className="ps-card-title">{tool.name}</h3>
            <p className="ps-card-desc">{tool.description}</p>
          </div>
          <span className="ps-status">
            {activated ? (
              <span className="ps-badge ps-badge--active"><CheckCircle size={13} /> Active</span>
            ) : subscribed ? (
              <span className="ps-badge ps-badge--ready"><Plus size={13} /> Ready</span>
            ) : (
              <Lock size={18} className="ps-lock" aria-label="Locked" />
            )}
          </span>
        </div>

        {activated ? (
          <button
            type="button"
            className="ps-btn ps-btn-primary"
            onClick={async () => {
              const verified = await verifySubscriptionBeforeLaunch(tool, projectId);
              if (verified) {
                navigate(tool.activeLink + "/" + projectId);
              }
            }}
          >
            Launch tool <ArrowRight size={16} />
          </button>
        ) : subscribed ? (
          <button
            type="button"
            className="ps-btn ps-btn-outline"
            onClick={() => activateToolForProject(tool)}
            disabled={isActivating}
          >
            {isActivating ? (
              <>
                <Loader size={16} className="ps-spin" />
                Activating...
              </>
            ) : (
              <>
                Activate for project <Plus size={16} />
              </>
            )}
          </button>
        ) : (
          <button
            type="button"
            className="ps-btn ps-btn-subtle"
            onClick={() => navigate("/services")}
          >
            Try this tool <ArrowRight size={16} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="ps-root">
      <SEO
        title="Select Your Project"
        description="Select or create a film project to get started with ProducerBuddy's full suite of production management tools."
        url="https://producerbuddy.pilomation.com/project-selector"
        robots="noindex, nofollow"
      />

      <div className="ps-main">
        {/* Suite header */}
        <header className="ps-suite">
          <span className="ps-suite-tile"><ToolIcon name="grid" size={28} /></span>
          <div className="ps-suite-text">
            <h2 className="ps-suite-title">Producer Buddy Suite</h2>
            <p className="ps-suite-sub">Powerful AI tools to plan, create, and manage your film production - all in one place.</p>
            <div className="ps-stats">
              <span className="ps-stat"><strong>{activatedToolIds.length}</strong> Active tools</span>
              <span className="ps-stat"><strong>{subscribedToolIds.length}</strong> Subscriptions</span>
            </div>
          </div>
          <div className="ps-search">
            <Search size={16} className="ps-search-icon" />
            <input
              type="text"
              placeholder="Search tools..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="ps-search-input"
              aria-label="Search tools"
            />
          </div>
        </header>

        {/* Category tabs */}
        <div className="ps-tabs" role="tablist" aria-label="Production stage">
          {Object.keys(toolsData).map((category) => (
            <button
              key={category}
              type="button"
              role="tab"
              aria-selected={selectedCategory === category}
              onClick={() => setSelectedCategory(category)}
              className={`ps-tab ${selectedCategory === category ? 'is-active' : ''}`}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="ps-section-head">
          <h3 className="ps-section-title">{selectedCategory} Tools</h3>
          <p className="ps-section-sub">{CATEGORY_COPY[selectedCategory]}</p>
        </div>

        {isLoading ? (
          <div className="ps-grid">
            <div className="ps-skeleton" />
            <div className="ps-skeleton" />
            <div className="ps-skeleton" />
            <div className="ps-skeleton" />
          </div>
        ) : (
          <>
            {orderedTools.length > 0 && (
              <div className="ps-grid">
                {orderedTools.map(tool => renderToolCard(tool))}
              </div>
            )}

            {filteredTools.length === 0 && (
              <div className="ps-empty">
                <p>No tools found matching "{searchQuery}"</p>
                <button type="button" className="ps-link" onClick={() => setSearchQuery('')}>Clear search</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* How our AI tools transform production */}
      <section className="ps-impact">
        <div className="ps-impact-bg" style={{ backgroundImage: `url(${footerBg})` }} />
        <div className="ps-impact-overlay" />
        <div className="ps-impact-content">
          <h2 className="ps-impact-title">How our AI tools transform production</h2>
          <p className="ps-impact-sub">Real results for real creators.</p>

          <div className="ps-impact-grid">
            {IMPACT_POINTS.map(point => (
              <div key={point.title} className="ps-impact-card">
                <span className={`ps-tile ps-tile--sm ps-tone-${point.tone}`}>
                  <ToolIcon name={point.icon} size={24} />
                </span>
                <div>
                  <h3 className="ps-impact-card-title">{point.title}</h3>
                  <p className="ps-impact-card-text">{point.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="ps-cta">
        <button
          type="button"
          className="ps-btn ps-btn-primary ps-btn-auto"
          onClick={() => navigate("/services")}
        >
          Explore full AI suite <ArrowRight size={16} />
        </button>
        <p className="ps-project-id">Project ID: {projectId}</p>
      </div>
    </div>
  );
};

export default ProductionToolsDashboard;